import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, CheckCircle, AlertCircle, FileText, Calculator } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { api } from '../../services/api';
import { TextArea } from '../../components/TextArea';

interface Resultado {
  id: string;
  id_acesso: string;
  id_questao: string;
  questao_titulo?: string;
  questao_enunciado: string;
  questao_tipo: 'multipla_escolha' | 'texto_livre';
  questao_pontuacao: number;
  resposta_dada: string;
  resposta_correta: string;
  pontuacao_obtida: number;
  status_correcao: 'pendente' | 'correta' | 'incorreta' | 'parcial';
  correcao_automatica: boolean;
  comentario_correcao?: string;
  corrigido_em?: string;
  militar_corretor?: {
    id: string;
    nome: string;
  };
}

interface ProvaAcesso {
  id: string;
  militar: {
    nick: string;
    patente: {
      patente: string;
    };
  };
  prova: {
    id: string;
    titulo: string;
    pontuacao_maxima: number;
  };
  data_finalizacao: string;
  tempo_gasto_minutos: number;
  resultado_divulgado?: boolean;
}

export const CorrecaoProvaPage: React.FC = () => {
  const { id_acesso } = useParams<{ id_acesso: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [processandoCorrecao, setProcessandoCorrecao] = useState(false);
  const [divulgandoResultado, setDivulgandoResultado] = useState(false);
  const [acessoInfo, setAcessoInfo] = useState<ProvaAcesso | null>(null);
  const [resultados, setResultados] = useState<Resultado[]>([]);
  const [correcoes, setCorrecoes] = useState<{
    [id_resultado: string]: {
      pontuacao_obtida: number;
      status_correcao: string;
      comentario_correcao: string;
    };
  }>({});

  useEffect(() => {
    carregarDados();
  }, [id_acesso]);

  const carregarDados = async () => {
    try {
      setLoading(true);

      // Carregar informações do acesso
      const acessoResponse = await api.get(`/provas/acessos/${id_acesso}`);
      console.log('Acesso:', acessoResponse.data);
      setAcessoInfo(acessoResponse.data);

      // Carregar respostas da prova
      const respostasResponse = await api.get(`/provas/acessos/${id_acesso}/respostas-correcao`);
      console.log('Respostas:', respostasResponse.data);

      // Carregar resultados para correção
      const resultadosResponse = await api.get(`/provas/acessos/${id_acesso}/resultados`);
      console.log('Resultados:', resultadosResponse.data);
      setResultados(resultadosResponse.data);

      // Se não há resultados mas há respostas, mostrar opção de processar correção
      if (resultadosResponse.data.length === 0 && respostasResponse.data.length > 0) {
        console.log('Há respostas mas não há resultados. Necessário processar correção automática.');
      } else if (respostasResponse.data.length === 0) {
        console.log('Não há respostas para esta prova.');
      }

      // Inicializar estado de correções com valores existentes
      const correcoesIniciais: any = {};
      resultadosResponse.data.forEach((resultado: Resultado) => {
        correcoesIniciais[resultado.id] = {
          pontuacao_obtida: resultado.pontuacao_obtida || 0,
          status_correcao: resultado.status_correcao || 'pendente',
          comentario_correcao: resultado.comentario_correcao || ''
        };
      });
      setCorrecoes(correcoesIniciais);

    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      enqueueSnackbar('Erro ao carregar dados da prova', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const processarCorrecaoAutomatica = async () => {
    try {
      setProcessandoCorrecao(true);

      // Primeiro, tentar processar correção automática (só funciona se houver respostas)
      try {
        await api.post(`/provas/acessos/${id_acesso}/correcao-automatica`, {
          processar_multipla_escolha: true,
          processar_texto_livre: false
        });

        enqueueSnackbar('Correção automática processada com sucesso!', { variant: 'success' });
      } catch (error: any) {
        // Se falhar por falta de respostas, tentar inicializar resultados
        if (error.response?.data?.message?.includes('Nenhuma resposta encontrada')) {
          console.log('Sem respostas cadastradas. Inicializando resultados para correção manual...');

          await api.post(`/provas/acessos/${id_acesso}/inicializar-resultados`);

          enqueueSnackbar(
            'Resultados inicializados para correção manual. A prova não possui respostas cadastradas.',
            { variant: 'warning' }
          );
        } else {
          throw error;
        }
      }

      // Recarregar dados
      await carregarDados();

    } catch (error: any) {
      console.error('Erro ao processar correção:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao processar correção',
        { variant: 'error' }
      );
    } finally {
      setProcessandoCorrecao(false);
    }
  };

  const handleCorrecaoChange = (
    id_resultado: string,
    field: 'pontuacao_obtida' | 'status_correcao' | 'comentario_correcao',
    value: any
  ) => {
    setCorrecoes(prev => ({
      ...prev,
      [id_resultado]: {
        ...prev[id_resultado],
        [field]: value
      }
    }));
  };

  const salvarCorrecao = async (resultado: Resultado) => {
    try {
      setSalvando(true);

      // Mesclar alterações pendentes com valores atuais do resultado
      const alteracoesPendentes = correcoes[resultado.id] || {} as {
        pontuacao_obtida?: number;
        status_correcao?: string;
        comentario_correcao?: string;
      };
      const correcao = {
        pontuacao_obtida: alteracoesPendentes.pontuacao_obtida ?? resultado.pontuacao_obtida,
        status_correcao: alteracoesPendentes.status_correcao ?? resultado.status_correcao,
        comentario_correcao: alteracoesPendentes.comentario_correcao ?? resultado.comentario_correcao ?? ''
      };

      await api.post('/provas/correcao-manual', {
        id_resultado: resultado.id,
        pontuacao_obtida: Number(correcao.pontuacao_obtida),
        status_correcao: correcao.status_correcao,
        comentario_correcao: correcao.comentario_correcao || null
      });

      // Atualizar apenas o resultado específico no estado local
      setResultados(prev => prev.map(r =>
        r.id === resultado.id
          ? {
              ...r,
              pontuacao_obtida: Number(correcao.pontuacao_obtida),
              status_correcao: correcao.status_correcao as 'pendente' | 'correta' | 'incorreta' | 'parcial',
              comentario_correcao: correcao.comentario_correcao || undefined,
              correcao_automatica: false
            }
          : r
      ));

      // Limpar as alterações pendentes deste resultado
      setCorrecoes(prev => {
        const { [resultado.id]: removed, ...rest } = prev;
        return rest;
      });

      enqueueSnackbar('Correção salva com sucesso!', { variant: 'success' });

    } catch (error: any) {
      console.error('Erro ao salvar correção:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao salvar correção',
        { variant: 'error' }
      );
    } finally {
      setSalvando(false);
    }
  };

  const salvarTodasCorrecoes = async () => {
    try {
      setSalvando(true);

      const resultadosPendentes = resultados.filter(
        r => r.status_correcao === 'pendente' || !r.correcao_automatica
      );

      for (const resultado of resultadosPendentes) {
        const correcao = correcoes[resultado.id];
        if (!correcao) continue;

        await api.post('/provas/correcao-manual', {
          id_resultado: resultado.id,
          pontuacao_obtida: Number(correcao.pontuacao_obtida),
          status_correcao: correcao.status_correcao,
          comentario_correcao: correcao.comentario_correcao || null
        });
      }

      // Atualizar todos os resultados no estado local
      setResultados(prev => prev.map(r => {
        const correcao = correcoes[r.id];
        if (correcao) {
          return {
            ...r,
            pontuacao_obtida: Number(correcao.pontuacao_obtida),
            status_correcao: correcao.status_correcao as 'pendente' | 'correta' | 'incorreta' | 'parcial',
            comentario_correcao: correcao.comentario_correcao || undefined,
            correcao_automatica: false
          };
        }
        return r;
      }));

      // Limpar todas as alterações pendentes
      setCorrecoes({});

      enqueueSnackbar('Todas as correções foram salvas!', { variant: 'success' });

    } catch (error: any) {
      console.error('Erro ao salvar correções:', error);
      enqueueSnackbar('Erro ao salvar correções', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  const divulgarResultado = async () => {
    try {
      setDivulgandoResultado(true);

      await api.put(`/provas/acessos/${id_acesso}/divulgar-resultado`);

      // Atualizar estado local
      setAcessoInfo(prev => prev ? { ...prev, resultado_divulgado: true } : null);

      enqueueSnackbar('Resultado divulgado com sucesso!', { variant: 'success' });
    } catch (error: any) {
      console.error('Erro ao divulgar resultado:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao divulgar resultado',
        { variant: 'error' }
      );
    } finally {
      setDivulgandoResultado(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pendente: <Badge className="bg-yellow-100 text-yellow-800">Pendente</Badge>,
      correta: <Badge className="bg-green-100 text-green-800">Correta</Badge>,
      incorreta: <Badge className="bg-red-100 text-red-800">Incorreta</Badge>,
      parcial: <Badge className="bg-orange-100 text-orange-800">Parcial</Badge>
    };
    return badges[status as keyof typeof badges] || null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!acessoInfo) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <p>Prova não encontrada</p>
        </div>
      </DashboardLayout>
    );
  }

  const questoesPendentes = resultados.filter(r => r.status_correcao === 'pendente').length;
  const pontuacaoTotal = resultados.reduce((soma, r) => {
    const pontuacao = correcoes[r.id]?.pontuacao_obtida || 0;
    return soma + Number(pontuacao);
  }, 0);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">{acessoInfo.prova.titulo}</h1>
              <p className="text-gray-600">
                {acessoInfo.militar.patente.patente} {acessoInfo.militar.nick}
              </p>
              <p className="text-sm text-gray-500">
                Finalizada em {new Date(acessoInfo.data_finalizacao).toLocaleString('pt-BR')} •
                Tempo: {acessoInfo.tempo_gasto_minutos} minutos
              </p>
            </div>

            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">
                {pontuacaoTotal.toFixed(1)}
              </div>
              <div className="text-sm text-gray-600">
                de {acessoInfo.prova.pontuacao_maxima} pontos
              </div>
            </div>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium">Total de Questões</p>
                  <p className="text-2xl font-bold">{resultados.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="text-sm font-medium">Pendentes</p>
                  <p className="text-2xl font-bold text-yellow-600">{questoesPendentes}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Calculator className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-sm font-medium">Percentual</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {Math.round((pontuacaoTotal / acessoInfo.prova.pontuacao_maxima) * 100)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Botões de ação */}
        <div className="flex gap-3 mb-6">
          {questoesPendentes > 0 && (
            <Button
              onClick={salvarTodasCorrecoes}
              disabled={salvando}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="mr-2 h-4 w-4" />
              {salvando ? 'Salvando...' : 'Salvar Todas as Correções'}
            </Button>
          )}

          {questoesPendentes === 0 && !acessoInfo?.resultado_divulgado && (
            <Button
              onClick={divulgarResultado}
              disabled={divulgandoResultado}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              {divulgandoResultado ? 'Divulgando...' : 'Divulgar Resultado'}
            </Button>
          )}

          {acessoInfo?.resultado_divulgado && (
            <Badge className="bg-green-100 text-green-800 border-green-200 px-4 py-2 text-sm">
              <CheckCircle className="mr-2 h-4 w-4" />
              Resultado Divulgado
            </Badge>
          )}
        </div>

        {/* Lista de questões */}
        {resultados.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
              <p className="text-gray-600 font-medium mb-2">Nenhum resultado de correção encontrado</p>
              <p className="text-sm text-gray-500 mb-4">
                Verifique no console do navegador (F12) se há respostas cadastradas para esta prova.
                Se houver respostas, clique no botão abaixo para processar a correção automática.
                Se não houver respostas, significa que a prova foi finalizada sem que o aluno respondesse às questões.
              </p>
              <div className="space-y-2">
                <Button
                  onClick={processarCorrecaoAutomatica}
                  disabled={processandoCorrecao}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {processandoCorrecao ? 'Processando...' : 'Tentar Processar Correção Automática'}
                </Button>
                <p className="text-xs text-gray-400">
                  Este botão só funcionará se houver respostas cadastradas
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {resultados.map((resultado, index) => (
            <Card key={resultado.id} className="overflow-hidden">
              <CardHeader className="bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">
                      Questão {index + 1}
                      {getStatusBadge(correcoes[resultado.id]?.status_correcao || resultado.status_correcao)}
                      {resultado.correcao_automatica && (
                        <Badge className="ml-2 bg-blue-100 text-blue-800">Automática</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-2 whitespace-pre-wrap">
                      {resultado.questao_enunciado}
                    </CardDescription>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-sm text-gray-600">Pontuação</div>
                    <div className="text-lg font-bold">{resultado.questao_pontuacao} pts</div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6">
                {/* Resposta do aluno */}
                <div className="mb-4">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Resposta do Aluno:</h4>
                  {resultado.questao_tipo === 'multipla_escolha' ? (
                    <div className="p-3 bg-blue-50 rounded-md">
                      <p className="text-sm">{resultado.resposta_dada}</p>
                    </div>
                  ) : (
                    <div className="p-3 bg-blue-50 rounded-md">
                      <p className="text-sm whitespace-pre-wrap">
                        {resultado.resposta_dada}
                      </p>
                    </div>
                  )}
                </div>

                {/* Resposta esperada (para texto livre) */}
                {resultado.questao_tipo === 'texto_livre' && resultado.resposta_correta && (
                  <div className="mb-4">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">Resposta Esperada:</h4>
                    <div className="p-3 bg-green-50 rounded-md">
                      <p className="text-sm whitespace-pre-wrap">{resultado.resposta_correta}</p>
                    </div>
                  </div>
                )}

                {/* Formulário de correção */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pontuação Obtida
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={resultado.questao_pontuacao}
                      step="0.1"
                      value={correcoes[resultado.id]?.pontuacao_obtida ?? resultado.pontuacao_obtida}
                      onChange={(e) => handleCorrecaoChange(resultado.id, 'pontuacao_obtida', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      disabled={resultado.correcao_automatica && resultado.questao_tipo === 'multipla_escolha'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={correcoes[resultado.id]?.status_correcao ?? resultado.status_correcao}
                      onChange={(e) => handleCorrecaoChange(resultado.id, 'status_correcao', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      disabled={resultado.correcao_automatica && resultado.questao_tipo === 'multipla_escolha'}
                    >
                      <option value="pendente">Pendente</option>
                      <option value="correta">Correta</option>
                      <option value="parcial">Parcial</option>
                      <option value="incorreta">Incorreta</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comentário (opcional)
                    </label>
                    <TextArea
                      name="comentario"
                      title=""
                      value={correcoes[resultado.id]?.comentario_correcao ?? resultado.comentario_correcao ?? ''}
                      onChange={(e) => handleCorrecaoChange(resultado.id, 'comentario_correcao', e.target.value)}
                      placeholder="Adicione um comentário sobre a correção..."
                      rows={3}
                    />
                  </div>
                </div>

                {/* Botão salvar individual */}
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={() => salvarCorrecao(resultado)}
                    disabled={salvando}
                    variant="outline"
                    size="sm"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Salvar Correção
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
